/**
 * CortisolEveningRitualCard — вечерният ритуал с едно докосване.
 * Три стъпки: дишане 4-7-8, светлините надолу, отметка за съня.
 * Данни: `evening` от /api/cortisol/program · POST /api/cortisol/evening-ritual
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Moon, CheckCircle2, Circle, Wind } from "lucide-react";
import { useAuth } from "@/App";
import CortisolBreathTimer from "@/components/stress/CortisolBreathTimer";

const QUALITY = [
  [1, "лошо"], [2, "слабо"], [3, "средно"], [4, "добре"], [5, "чудесно"],
];

export default function CortisolEveningRitualCard({ evening, isDark = false, onChange }) {
  const { API } = useAuth();
  const [showBreath, setShowBreath] = useState(false);
  const [bedtime, setBedtime] = useState(evening?.bedtime || "22:30");

  if (!evening) return null;
  const border = isDark ? "border-white/10" : "border-slate-200";

  const save = async (patch, msg) => {
    try {
      const { data } = await axios.post(`${API}/cortisol/evening-ritual`, patch);
      onChange?.(data);
      if (msg) toast.success(msg);
    } catch { toast.error("Не се записа"); }
  };

  const done = evening.done_count || 0;

  return (
    <div className={`rounded-2xl border p-4 ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}`}
      data-testid="cortisol-evening-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[13px] font-extrabold inline-flex items-center gap-1.5">
          <Moon className="w-4 h-4 text-indigo-400" /> Вечерен ритуал
        </span>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full
          ${done === 3 ? "bg-emerald-500/15 text-emerald-600" : "bg-indigo-500/15 text-indigo-500"}`}
          data-testid="cortisol-evening-progress">
          {done}/3 за днес
        </span>
      </div>
      <div className="text-[11px] opacity-75 mt-0.5">
        Три минути преди сън свалят вечерния кортизол повече от всяко усилие през деня.
      </div>

      <div className="mt-3 space-y-1.5">
        {/* 1. дишане */}
        <div className={`p-2 rounded-lg border ${border} flex items-start gap-2`}
          data-testid="cortisol-evening-breath">
          <button onClick={() => save({ breath_done: !evening.breath_done })}
            className="mt-0.5" data-testid="cortisol-evening-breath-toggle">
            {evening.breath_done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              : <Circle className="w-4 h-4 opacity-50" />}
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold">Дишане 4-7-8, 3 минути</div>
            <div className="text-[10px] opacity-70">
              Вдишай 4, задръж 7, издишай 8. Таймерът брои и звучи сам.
            </div>
            <button onClick={() => setShowBreath((s) => !s)}
              className="mt-1 text-[11px] font-bold text-emerald-600 inline-flex items-center gap-1"
              data-testid="cortisol-evening-open-breath">
              <Wind className="w-3.5 h-3.5" /> {showBreath ? "Скрий таймера" : "Пусни таймера"}
            </button>
          </div>
        </div>

        {showBreath && (
          <CortisolBreathTimer isDark={isDark} initialMode="m478"
            onSaved={(data) => { onChange?.(data); save({ breath_done: true }); }} />
        )}

        {/* 2. светлините */}
        <div className={`p-2 rounded-lg border ${border} flex items-start gap-2`}
          data-testid="cortisol-evening-lights">
          <button onClick={() => save({ lights_done: !evening.lights_done })}
            className="mt-0.5" data-testid="cortisol-evening-lights-toggle">
            {evening.lights_done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              : <Circle className="w-4 h-4 opacity-50" />}
          </button>
          <div className="min-w-0">
            <div className="text-[11px] font-bold">Светлините надолу, без екрани</div>
            <div className="text-[10px] opacity-70">
              Приглуши осветлението час преди лягане и остави телефона извън спалнята.
            </div>
          </div>
        </div>

        {/* 3. сънят */}
        <div className={`p-2 rounded-lg border ${border}`} data-testid="cortisol-evening-sleep">
          <div className="text-[11px] font-bold flex items-center gap-1.5">
            {evening.bedtime ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              : <Circle className="w-4 h-4 opacity-50" />}
            Отметка за съня
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <input type="time" value={bedtime} onChange={(e) => setBedtime(e.target.value)}
              className={`px-2 py-1 rounded-lg text-[12px] border ${
                isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
              data-testid="cortisol-evening-bedtime" />
            <button onClick={() => save({ bedtime }, "Часът на лягане е записан")}
              className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-white
                         bg-gradient-to-r from-indigo-500 to-violet-600"
              data-testid="cortisol-evening-bedtime-save">
              Записах лягането
            </button>
          </div>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {QUALITY.map(([v, label]) => (
              <button key={v} onClick={() => save({ sleep_quality: v })}
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold border
                  ${evening.sleep_quality === v
                    ? "border-indigo-500 bg-indigo-500/15 text-indigo-500"
                    : `${border} opacity-70`}`}
                data-testid={`cortisol-evening-quality-${v}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
